import React, { BaseSyntheticEvent, FC, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  IonSpinner,
  IonRow,
  IonCol,
  IonList,
  IonItemDivider,
  IonLabel,
  IonToggle,
  IonItem,
} from '@ionic/react'
import { chevronBackOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { getPatientTimelineSelector } from '../../../timeline/selectors/patientTimeline.selector'
import { AccordianComponent } from '../../../../components/accordian/Accordian.components'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { IPnProcedureOptions, IPnProcedureMetadataList, IPnProcedureState } from '../store/pnProcedure.types'
import { getPnProcedureSelector } from '../selectors/pnProcedure.selector'
import { setPTA, setPnProcedure, savePnProcedure } from '../store/pnProcedure.actions'
import { getPnProcedureOptionsSelector } from '../../../options/selectors'
import TimesRow from '../../../times/timesRow/TimesRow.component'
import { PnProcedureConstants } from '../store/pnProcedure.constants'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialAddPnProcedure, initialPnProcedureState } from '../store/pnProcedure.reducers'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { IPertinentNegativeProcedureOptionsState } from '../../../options/store/options.types'
import { randomNumber } from '../../../../utilities'
import './PnProcedure.page.css'
import { isValidZollNowDate, isValidZollTime } from '../../../../utilities/dateTime.util'
import { getPTADateTimeErrorMessage, isProcDateTimePTA } from '../../../../utilities/procedures.util'
import { getTimesSelector } from '../../../times/selectors/getTimes.selector'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { goBack } from '../../../../utilities/navigation.util'
import { IOption, IPNNVOption } from '../../../../common/common.types'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { getCurrentDate, getCurrentTime } from '../../../../utilities'

const PnProcedure: FC = () => {
  const pnProcedureState: IPnProcedureState = useSelector(getPnProcedureSelector)
  const pnProcedureOptions: IPertinentNegativeProcedureOptionsState = useSelector(getPnProcedureOptionsSelector)
  const timelineState = useSelector(getPatientTimelineSelector)
  const { pertinentNegativeProcedureOptions } = pnProcedureOptions
  const { procedurePNNVs, date, time, pta } = pnProcedureState
  const { procedureLoader } = timelineState
  const { atPatientDate, atPatientTime, onSceneDate, onSceneTime } = useSelector(getTimesSelector)

  const { DISPLAY_SECONDS }: IServiceConfig = useSelector(getServiceConfigSelector)
  const displaySeconds = !!DISPLAY_SECONDS

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()

  // Temporary fix for MOB-17564 until the more permanent fix of separating out the loading states for all procedure types
  // (To prevent the loading of one procedure from causing other procedures to load in the background)
  const isPageActive = history.location?.pathname === TabConstants.PERTINENT_NEGATIVE_PROCEDURE

  useEffect(() => {
    if (pta && isValidZollNowDate(date)) {
      const errMsg = getPTADateTimeErrorMessage(
        atPatientDate,
        atPatientTime,
        onSceneDate,
        onSceneTime,
        date,
        time,
        displaySeconds
      )
      if (errMsg) {
        dispatch(showAlert({ message: errMsg }))
        dispatch(setPTA(false))
      } else if (!pta && isValidZollNowDate(date) && isValidZollTime(time)) {
        if (isProcDateTimePTA(atPatientDate, atPatientTime, onSceneDate, onSceneTime, date, time, displaySeconds))
          dispatch(setPTA(true))
      }
    }
  }, [date, time, pta])

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline']
    const currentState = filterOutObjectKeys(pnProcedureState, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialPnProcedureState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState) && procedurePNNVs?.length) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onPTAClick = () => {
    const isPTA = !pta
    dispatch(setPTA(isPTA))
  }

  const handleChange = (option: IPNNVOption, categoryId: number, category: string) => {
    const selectedOption = procedurePNNVs?.find((proc) => Number(proc?.nemsisCode) === categoryId)
    const obj = {
      ...initialAddPnProcedure,
      id: selectedOption ? selectedOption.id : randomNumber(),
      date: pnProcedureState?.date,
      time: pnProcedureState?.time,
      pta: pnProcedureState?.pta,
      type: category,
      nemsisCodePNNV: {
        pn: option.pn ? option.pn : '',
        nv: option.nv ? option.nv : '',
      },
      nemsisCode: String(categoryId),
    }
    dispatch(setPnProcedure(obj))
  }

  const getCheckMarkValue = (pnProcMetadata: IPnProcedureMetadataList): boolean => {
    return pnProcMetadata?.some((pnProc) => {
      return pnProc?.options?.some((category) => {
        return procedurePNNVs?.some(
          (pertNeg) =>
            Number(pertNeg?.nemsisCode) === pnProc?.id &&
            (Number(pertNeg?.nemsisCodePNNV?.pn) === category.id || Number(pertNeg?.nemsisCodePNNV?.nv) === category.id)
        )
      })
    })
  }

  const getGroupContent = (pnProcMetadata: IPnProcedureMetadataList, category: string) => {
    return pnProcMetadata?.map((pnProc) => {
      const filteredOption: IOption | null | undefined = pnProc?.options?.find((op) =>
        procedurePNNVs?.find(
          (pertNeg) =>
            Number(pertNeg?.nemsisCode) === pnProc?.id &&
            (Number(pertNeg?.nemsisCodePNNV?.pn) === op?.id || Number(pertNeg?.nemsisCodePNNV?.nv) === op?.id)
        )
      )
      let selectedOption: IPNNVOption | null = null
      if (filteredOption && filteredOption.id && filteredOption.name) {
        selectedOption = {
          id: filteredOption.id,
          name: filteredOption.name,
          pn: '',
          nv: '',
        }
        const key = 'nemsisCode' as keyof IOption
        if (filteredOption[key]) {
          if (filteredOption.isPN) {
            selectedOption.pn = String(filteredOption[key])
          } else if (filteredOption.isNV) {
            selectedOption.nv = String(filteredOption[key])
          }
        }
      }
      return (
        <React.Fragment key={pnProc.id}>
          <IonRow>
            <IonCol className='picklist ion-no-padding font-roboto ion-no-margin'>
              <SingleSelect
                dataTestID={`Neg_${pnProc.id}`}
                action={() => {
                  /**/
                }}
                pnnvAction={(option: IPNNVOption, categoryId: number) => handleChange(option, categoryId, category)}
                listHeader={pnProc.name}
                options={pnProc.options}
                label={pnProc.name}
                selectedPNNVOption={selectedOption}
                dispatchData={pnProc.id}
                showPNNVAsNormals={true}
              />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
        </React.Fragment>
      )
    })
  }
  const memoizedPertinentNegativeProcedureContent = useMemo(() => {
    return pertinentNegativeProcedureOptions?.map((data: IPnProcedureOptions) => (
      <IonRow key={data?.category}>
        <IonCol className='ion-no-padding'>
          <AccordianComponent
            dataTestId={data?.category}
            heading={data?.category}
            content={getGroupContent(data?.categoryOptions, data?.category)}
            isCheckMark={getCheckMarkValue(data?.categoryOptions)}
          />
        </IonCol>
      </IonRow>
    ))
  }, [procedurePNNVs])

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'pnProcedure/SET_TIME', time: time })
              dispatch({ type: 'pnProcedure/SET_DATE', date: date })

              dispatch(savePnProcedure())

              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: pnProcedureState.key,
                rowActionType: TimelineConstants.SET_PN_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const renderContent = () => {
    return (
      <>
        {procedureLoader ? (
          <IonSpinner className='procedure-loader' data-testid='Spinner' />
        ) : (
          <IonList>
            <IonItemDivider color='light' className='ion-padding'>
              <IonLabel className='grid-item-divider-label' color='medium'>
                {t('pnProcedure.addPertNegativeProcedure')}
              </IonLabel>
            </IonItemDivider>

            <IonRow>
              <IonCol className='picklist'>
                <TimesRow
                  elementId='Date-Time'
                  header={t('procedure.header.dateTime')}
                  time={time}
                  date={date}
                  timeAction={PnProcedureConstants.SET_TIME}
                  dateAction={PnProcedureConstants.SET_DATE}
                  currentDateTimeAction={PnProcedureConstants.SET_DATE_TIME}
                  subHeader={''}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol>
                <IonItem lines='none'>
                  <IonLabel>{t('procedure.header.pta')}</IonLabel>
                  <IonToggle
                    data-testid='PTA'
                    slot='start'
                    name='pta'
                    checked={pta}
                    onClick={onPTAClick}
                    color='zoll-blue-secondary'
                  />
                </IonItem>
              </IonCol>
            </IonRow>

            <IonItemDivider color='light' />
            {memoizedPertinentNegativeProcedureContent}
          </IonList>
        )}
      </>
    )
  }

  return (
    <IonPage id='PnProcedurePage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('pnProcedure.pertNegativeProcedure')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen color={'light'}>
        {isPageActive && renderContent()}
      </IonContent>
    </IonPage>
  )
}

export default React.memo(PnProcedure)
